import * as React from 'react';
import { TextField } from '@mui/material';

import 'react-date-range/dist/theme/default.css'; // main style file
import 'react-date-range/dist/styles.css'; // theme css file
import './index.css'; // theme css file

import { DateRange } from 'react-date-range';
import { formatDateWithoutTime } from '../../helper/formatDate';

function useWindowSize() {
  const [size, setSize] = React.useState([0, 0]);
  React.useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

/**
 * Custom date range component.
 *
 * @param {object} props - Component props.
 * @param {Date} props.min - Minimum selectable date.
 * @param {Date} props.max - Maximum selectable date.
 * @param {function} props.fromOnChange - Callback function when the 'from' date changes.
 * @param {function} props.toOnChange - Callback function when the 'to' date changes.
 * @param {Date} props.from - Selected 'from' date.
 * @param {Date} props.to - Selected 'to' date.
 * @param {string} props.fromError - Error message for the 'from' date.
 * @param {string} props.toError - Error message for the 'to' date.
 * @param {boolean} props.errors - Indicates whether errors are present.
 * @param {string} props.label - Label for the component.
 * @returns {JSX.Element} - DateRangeComponent JSX element.
 */
export default function DateRangeComponent({
  min,
  max,
  fromOnChange,
  toOnChange,
  from,
  to,
  fromError,
  toError,
  errors,
  label,
}) {
  const [width] = useWindowSize();

  // date state

  const [range, setRange] = React.useState([
    {
      //remove the null if its not working
      startDate: from ? new Date(from) : new Date(),
      endDate: to ? new Date(to) : new Date(),
      key: 'selection',
    },
  ]);

  React.useEffect(() => {
    setRange([
      {
        //remove the null if its not working
        startDate: from ? new Date(from) : new Date(),
        endDate: to ? new Date(to) : new Date(),
        key: 'selection',
      },
    ]);
  }, [from, to]);

  // open close
  const [open, setOpen] = React.useState(false);

  // get the target element to toggle
  const refOne = React.useRef(null);

  React.useEffect(() => {
    // isNaN(Date.parse(from)) && fromOnChange(range[0].startDate)
    // isNaN(Date.parse(to)) && toOnChange(range[0].endDate)
    // event listeners
    document.addEventListener('keydown', hideOnEscape, true);
    document.addEventListener('click', hideOnClickOutside, true);

    return () => {
      document.removeEventListener('keydown', hideOnEscape);
      document.removeEventListener('click', hideOnClickOutside);
    };
  }, []);

  const calendarSize = React.useMemo(() => {
    return width <= 750 ? 1 : 2;
  }, [width]);

  // hide dropdown on ESC press
  const hideOnEscape = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Hide on outside click
  const hideOnClickOutside = (e) => {
    if (refOne.current && !refOne.current.contains(e.target)) {
      setOpen(false);
    }
  };

  // Callback function when the date range changes
  function onChange(item) {
    setRange([item.selection]);
    fromOnChange(item.selection.startDate);
    toOnChange(item.selection.endDate);
  }

  // Checks for errors and returns the appropriate error message
  function checkErrors() {
    if (errors) return fromError !== undefined && toError !== undefined ? 'Required' : null;
    if (fromError !== undefined) return fromError;
    if (toError !== undefined) return toError;
    return null;
  }

  return (
    <div className="calendarWrap">
      <TextField
        sx={{ width: '100%' }}
        readOnly
        value={!!checkErrors() ? '' : `${formatDateWithoutTime(from)} - ${formatDateWithoutTime(to)}`}
        onClick={() => setOpen((open) => !open)}
        label={label ? label : 'Date Range'}
        error={!!checkErrors()}
        helperText={checkErrors()}
        className="calender-input"
      />

      <div ref={refOne}>
        {open && (
          <DateRange
            onChange={onChange}
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            editableDateInputs={true}
            dateDisplayFormat="dd-MM-yyyy"
            endDatePlaceholder="To"
            startDatePlaceholder="From"
            minDate={min}
            maxDate={max}
            ranges={range}
            months={calendarSize}
            direction="horizontal"
            className="calendarElement"
          />
        )}
      </div>
    </div>
  );
}
